import { Button, Grid, Theme } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  useAppDispatch,
  useAppSelector,
  useShallowSelector,
} from '../../app/hooks';
import useAgentInterception from '../../hooks/agentInterception/useAgentInterception.hooks';
import { dialogActions } from '../../reducers/dialogSlice';
import {
  AgentInterceptionType,
  InfoTransmissionMessage,
  RestaurantStaffInterventionStatus,
  StaffInterceptionType,
  messagingActions,
} from '../../reducers/messagingSlice';
import { orderActions } from '../../reducers/orderSlice';
import { OrderStatus } from '../../reducers/orderSlice.constants';
import { promptsSelector, selectTotalConstraint } from '../../selectors/menu';
import { selectRestaurantStaffIntervention } from '../../selectors/message';
import {
  selectCurrentTransactionCoupons,
  selectOrderValues,
} from '../../selectors/order';
import {
  selectActiveRestaurantDetails,
  selectActiveRestaurantIsBilingual,
} from '../../selectors/restaurant';
import { getMessageWithTotal } from '../../utils/TTSMessage';
import { activeCartSelector } from '../../utils/cart';
import Colors from '../../utils/color';
import { LANGUAGES, LANGUAGE_SWITCH_INTENT_CARD } from '../../utils/constants';
import useSendMessageHooks from '../../hooks/useSendMessage/useSendMessage.hooks';
import { pickNextIndex } from '../../utils/common';
import useLanguages from '../../hooks/useLanguages.hooks';
import LanguageSwitch from '../LangaugeSwitch';
import { IntentCard, VISIBILITY_FACTOR } from '../../types/intent';
import useReadback from '../../hooks/useReadback/useReadback.hooks';
import useSlashCommandReminder from '../../hooks/useSlashCommandReminder.hooks';
import { selectIntentCards } from '../../selectors/menu';
import { DEFAULT_INTENT_CARDS } from '../../constants/intents';

const useStyles = makeStyles((theme: Theme) => ({
  container: {
    background: Colors.porcelain,
    padding: '10px',
    height: 'auto',
    alignSelf: 'flex-end',
    borderTop: `2px solid ${Colors.alto}`,
  },
  card: {
    height: '48px',
    textAlign: 'center',
    textTransform: 'uppercase',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '100%',
    lineHeight: `${theme.spacing(2.25)} !important`,
    fontWeight: 'bold !important',
  },
  languageSwitch: {
    display: 'flex',
    backgroundColor: '#ffffff',
    borderRadius: '24px',
    padding: '4px',
    height: '48px',
    alignItems: 'center',
    justifyContent: 'space-between',
    position: 'relative',
    width: '100%',
  },
  languageOption: {
    flex: 1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    height: '40px',
    borderRadius: '20px',
    cursor: 'pointer',
    transition: 'all 0.3s ease',
    fontSize: '12px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    zIndex: 1,
    '&.disabled': {
      cursor: 'not-allowed',
      opacity: 0.5,
    },
  },
  languageOptionActive: {
    backgroundColor: 'rgb(0, 107, 166)',
    color: 'white',
  },
  languageOptionInactive: {
    backgroundColor: 'transparent',
    color: '#4f5153',
  },
}));

export default function IntentsSection() {
  const classes = useStyles();
  const dispatch = useAppDispatch();
  const cartActive = useSelector(activeCartSelector);
  const prompts = useSelector(promptsSelector);
  const bmTotalPrompt = useSelector(selectTotalConstraint);
  const currentTransactionId = useAppSelector(
    (state) => state.order.currentTransactionId
  );
  const currentTransactionCoupons = useAppSelector(
    selectCurrentTransactionCoupons
  );
  const currentTransactionCouponStatus = Object.values(
    currentTransactionCoupons
  );
  const allCouponApplied = useMemo(() => {
    return currentTransactionCouponStatus.every(
      (status) => status === OrderStatus.completed
    );
  }, [currentTransactionCouponStatus]);

  const transactions = useAppSelector((state) => state.order.transactions);
  const ITEM_BY_ITEM = useAppSelector((state) => state.config.ITEM_BY_ITEM);
  const completeClickCount = useAppSelector(
    (state) => state.order.completeClickCount
  );
  const { total } = useShallowSelector(selectOrderValues);
  const restaurantStaffIntervention = useSelector(
    selectRestaurantStaffIntervention
  );

  const { restaurantCode, restaurantName } = useSelector(
    selectActiveRestaurantDetails
  );
  const isBilingual = useAppSelector(selectActiveRestaurantIsBilingual);
  const [downTime, setDownTime] = useState(0);
  const intentsStatus = useAppSelector((state) => state.dialog.intentsStatus);
  const DISABLE_INTENT_INTERVAL = useAppSelector(
    (state) => state.config.DISABLE_INTENT_INTERVAL
  );

  const currentLanguage = useAppSelector(
    (state) => state.language.currentLanguage
  );
  const isSpanish = currentLanguage === LANGUAGES.SPANISH;
  const intentCardsFromBM = useAppSelector(selectIntentCards);

  // Use intent cards from BM if available, otherwise fallback to default
  const INTENT_CARDS = intentCardsFromBM ?? DEFAULT_INTENT_CARDS;

  const numOfIntents = INTENT_CARDS.length;
  const { sendMessage } = useSendMessageHooks();
  const { sendAgentInterception } = useAgentInterception();
  const { sendLanguageSwitch } = useLanguages();
  const { handleReadBack } = useReadback();
  const { showSlashCommandReminder } = useSlashCommandReminder();

  useEffect(() => {
    dispatch(
      dialogActions.initIntentsStatus(
        new Array(numOfIntents).fill({
          clickable: true,
          lastMessageIndex: undefined,
        })
      )
    );
  }, [dispatch, numOfIntents]);

  const onIntentDown = () => {
    setDownTime(new Date().valueOf());
  };

  const onIntentUp = (intent: IntentCard, idx: number) => {
    const timePressed = Math.floor((new Date().valueOf() - downTime) / 100);
    setDownTime(0);
    onIntentsClick(
      intent.title,
      intent.messages,
      timePressed,
      intent.intent,
      idx
    );
  };

  const setIntentClickable = (idx: number, status: boolean) => {
    dispatch(dialogActions.setIntentClickable({ idx, status }));
  };

  const sendAgentInterventionEvent = () => {
    sendAgentInterception({
      data: {
        type: AgentInterceptionType.TTS_SENT,
      },
    });
  };

  const sendMetric = (message: string) => {
    const payload: Partial<InfoTransmissionMessage> = {
      data: { type: 'METRIC', message: message },
    };
    dispatch(messagingActions.sendInfo(payload as any));
  };

  const languageSwitch = function (intent: IntentCard) {
    sendAgentInterventionEvent();
    sendMetric(intent.intent + ' intent clicked');
    return sendLanguageSwitch(
      isSpanish ? LANGUAGES.ENGLISH : LANGUAGES.SPANISH
    );
  };

  const onIntentsClick = (
    title: string,
    messages: string[],
    cTimer: number,
    intent: string,
    idx: number
  ) => {
    setIntentClickable(idx, false);
    setTimeout(() => {
      setIntentClickable(idx, true);
    }, DISABLE_INTENT_INTERVAL);

    sendAgentInterventionEvent();
    sendMetric(title || intent + ' intent clicked');
    if (
      title === 'Request Staff' &&
      restaurantCode &&
      restaurantStaffIntervention === RestaurantStaffInterventionStatus.initial
    ) {
      dispatch(
        messagingActions.setRestaurantStaffIntervention(
          RestaurantStaffInterventionStatus.open
        )
      );
      dispatch(
        messagingActions.sendStaffInterception({
          data: {
            type: StaffInterceptionType.AGENT_REQUESTED_STAFF,
          },
        })
      );
    }

    if (title === 'Read Back') {
      handleReadBack({ timePressed: cTimer });
      return;
    }

    if (title === 'Confirm') {
      showSlashCommandReminder();
    }

    if (title === 'Complete') {
      dispatch(orderActions.increaseCompleteClickCount());
    }

    const messageIndex = pickNextIndex(
      messages.length,
      intentsStatus[idx].lastMessageIndex
    );
    let message = '';
    if (messageIndex !== undefined) {
      message = messages[messageIndex];
      dispatch(
        dialogActions.setIntentLastMessageIdx({
          idx,
          lastMessageIndex: messageIndex,
        })
      );
    }

    if (['Total', 'Complete'].includes(title)) {
      if (title === 'Complete') {
        const promptMessageFromMenu = bmTotalPrompt
          ? bmTotalPrompt['total-template']
          : prompts['total-template'];
        if (promptMessageFromMenu) {
          message = promptMessageFromMenu.phrase;
        }
      }

      message = getMessageWithTotal({
        message,
        ITEM_BY_ITEM,
        currentTransactionId,
        transactions,
        completeClickCount,
        total,
        allCouponApplied,
        title,
      });
    }
    message = message.replaceAll('{RESTAURANT_NAME}', restaurantName || '');
    sendMessage(message, { intent, timeDelta: cTimer });
  };

  const intents = INTENT_CARDS.filter((intent) => {
    if (intent.visibility !== undefined) {
      switch (intent.visibility) {
        case VISIBILITY_FACTOR.CART_ACTIVE:
          return cartActive;
        case VISIBILITY_FACTOR.CART_EMPTY:
          return !cartActive;
        default:
          return true;
      }
    } else {
      return true;
    }
  });
  return (
    <div id="intentsSection" className={classes.container}>
      <Grid container spacing={1}>
        {isBilingual ? (
          <Grid item xs={LANGUAGE_SWITCH_INTENT_CARD.cardSize}>
            <LanguageSwitch
              classes={classes}
              isSpanish={isSpanish}
              onIntentUp={languageSwitch}
              intentCard={LANGUAGE_SWITCH_INTENT_CARD}
            />
          </Grid>
        ) : null}
        {intents.map((intentCard, idx) => (
          <Grid key={idx} item xs={intentCard.cardSize}>
            <Button
              key={`intent-${idx}`}
              onMouseDown={() => onIntentDown()}
              onMouseUp={() => onIntentUp(intentCard, idx)}
              className={classes.card}
              style={{
                backgroundColor: !intentsStatus[idx]?.clickable
                  ? Colors.alto2
                  : intentCard.color,
                color: intentCard.textColor,
              }}
              disabled={!intentsStatus[idx]?.clickable}
            >
              {intentCard.title}
            </Button>
          </Grid>
        ))}
      </Grid>
    </div>
  );
}
